import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { FormSection, DefinitionList, DataPair, StatusPill } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { getConsumable, Consumable, isChemical } from '../../services/consumableService';
import { formatDate } from '../../dateFormat';

const ConsumableCatalogDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [doc, setDoc] = useState<Consumable | null>(null);

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('consumables.title'), to: '/consumables/catalog' },
    { label: t('consumables.catalog.title'), to: '/consumables/catalog' },
    { label: doc?.productName ?? '' },
  ]);

  useEffect(() => {
    if (!id) return;
    getConsumable(id).then(setDoc).catch(() => setDoc(null));
  }, [id]);

  if (!doc) return null;

  return (
    <div data-testid="consumables-catalog-detail">
      <PageHeader
        title={doc.productName}
        meta={
          doc.needsReview ? (
            <StatusPill variant="warn">
              {t('consumables.catalog.needsReviewBadge')}
            </StatusPill>
          ) : (
            <span>{doc.consumableType}</span>
          )
        }
        actions={
          isChemical(doc) ? (
            <Link to={`/protection/products/${doc._id}`} className="pat-btn pat-btn--primary">
              {t('consumables.catalog.detail.openChemicalDetails')}
            </Link>
          ) : null
        }
      />

      <FormSection
        id="base"
        title={t('consumables.catalog.list.columns.productName')}
      >
        <DefinitionList columns={2}>
          <DataPair
            label={t('consumables.catalog.list.columns.productName')}
            value={doc.productName}
          />
          <DataPair
            label={t('consumables.catalog.list.columns.consumableType')}
            value={doc.consumableType}
          />
          <DataPair
            label={t('consumables.catalog.list.columns.updatedAt')}
            value={doc.updatedAt ? formatDate(doc.updatedAt) : ''}
          />
        </DefinitionList>
      </FormSection>

      {isChemical(doc) ? (
        <FormSection
          id="chemical"
          title={t('consumables.catalog.filters.type.chemical')}
        >
          <DefinitionList columns={2}>
            {doc.lNumber ? <DataPair label="L-Number" value={doc.lNumber} /> : null}
            {doc.activeIngredients && doc.activeIngredients.length > 0 ? (
              <DataPair
                label="Active ingredient(s)"
                value={doc.activeIngredients
                  .map(ai => `${ai.name} (${ai.value} ${ai.unit})`)
                  .join(', ')}
              />
            ) : null}
            {doc.category ? <DataPair label="Category" value={doc.category} /> : null}
            {doc.dosePer100L != null ? (
              <DataPair
                label="Dose per 100L"
                value={`${doc.dosePer100L} ${doc.dosePer100LUnit ?? ''}`}
              />
            ) : null}
            {doc.pestTreated ? <DataPair label="Pest treated" value={doc.pestTreated} /> : null}
            {doc.formulation ? <DataPair label="Formulation" value={doc.formulation} /> : null}
          </DefinitionList>
        </FormSection>
      ) : (
        <FormSection id="discriminator-notice" title={doc.consumableType}>
          <p className="pat-field__help">
            {t('consumables.catalog.detail.discriminatorNotice', { type: doc.consumableType })}
          </p>
        </FormSection>
      )}
    </div>
  );
};

export default ConsumableCatalogDetailPage;
